import { LuIcon, Text } from "@overmap-ai/blocks"
import { type AssetType, EMPTY_ARRAY, type Stored, toUuidArray } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { AssetTypeMultiSelect } from "../../../components/DropdownMultiSelects/AssetTypeMultiSelect"
import { AssetTypeSelect } from "../../../components/DropdownSelects/AssetTypeSelect"
import { AssetTypeIcon, AssetTypeIcons } from "../../../components/Icons/AssetTypeIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { AssetTypeFilterDef } from "./AssetTypeFilterDef"

type AssetTypeFilterCellProps = FilterDefRenderProps<AssetTypeFilterDef>

export const AssetTypeFilterCell = (props: AssetTypeFilterCellProps) => {
	const { filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)
	const assetTypes = filterDef.assetTypes
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedAssetType = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return assetTypes.find(({ uuid }) => uuid === filterValue)
	}, [assetTypes, filterValue, filterDef.modifiers.equals])

	const selectedAssetTypes = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined
		const set = new Set(filterValue)
		return assetTypes.filter(({ uuid }) => set.has(uuid))
	}, [assetTypes, filterValue, filterDef.modifiers.includes])

	const handleValueSelect = useCallback(
		(assetType?: Stored<AssetType>) => {
			filter.setFilterValue(assetType?.uuid)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(assetTypes: Stored<AssetType>[]) => {
			if (assetTypes.length > 0) {
				filter.setFilterValue(assetTypes.map((assetType) => assetType.uuid))
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				return (
					<AssetTypeSelect
						required={false}
						assetTypes={assetTypes}
						value={selectedAssetType}
						onValueChange={handleValueSelect}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							{selectedAssetType !== undefined ? (
								<>
									<AssetTypeIcon assetTypeId={selectedAssetType.uuid} />
									<Text className="truncate">{selectedAssetType.name}</Text>
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</AssetTypeSelect>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedAssetTypes ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<AssetTypeIcon assetTypeId={selectedAssetTypes![0].uuid} />
									<Text className="truncate">{selectedAssetTypes![0].name}</Text>
								</>
							)
						default:
							return (
								<>
									<AssetTypeIcons assetTypeIds={toUuidArray(selectedAssetTypes!)} />
									{`${selectedAssetTypes!.length} asset types`}
								</>
							)
					}
				}

				return (
					<AssetTypeMultiSelect
						assetTypes={assetTypes}
						values={selectedAssetTypes}
						onValuesChange={handleValuesChange}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
						</FilterCellButton>
					</AssetTypeMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.assetType} />
				Asset type
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
